// Tipos de sorteo (cada uno genera ticket independiente)
export type SorteoType = 
  | 'TICA'
  | 'REVENTADO' 
  | 'MEGA'
  | 'MONAZOS' 
  | 'FECHAS' 
  | 'PARLEY';

// Configuración de cada sorteo
export interface SorteoConfig {
  id: SorteoType;
  label: string;
  shortLabel: string;
  color: string;
}

export const SORTEO_CONFIG: SorteoConfig[] = [
  // Nota: mantenemos el id 'TICA' por compatibilidad interna,
  // pero el nombre visible pasa a ser "Normal".
  { id: 'TICA', label: 'Normal', shortLabel: 'NOR', color: 'bg-blue-600' },
  { id: 'REVENTADO', label: 'Reventado', shortLabel: 'REV', color: 'bg-orange-600' },
  { id: 'MEGA', label: 'Mega', shortLabel: 'MEG', color: 'bg-red-600' },
  { id: 'MONAZOS', label: 'Monazos', shortLabel: 'MON', color: 'bg-purple-600' },
  { id: 'FECHAS', label: 'Fechas', shortLabel: 'FEC', color: 'bg-green-600' },
  { id: 'PARLEY', label: 'Parley', shortLabel: 'PAR', color: 'bg-pink-600' },
];

// Modalidades de apuesta para el POS
export type BetModalidad = 
  | 'NORMAL' 
  | 'REVENTADO' 
  | 'MEGA_REVENTADO' 
  | 'MONAZOS' 
  | 'FECHAS' 
  | 'PARLEY';

// Configuración de cada modalidad
export interface ModalidadConfig {
  id: BetModalidad;
  label: string;
  shortLabel: string;
  color: string;
  bgColor: string;
}

export const MODALIDAD_CONFIG: ModalidadConfig[] = [
  { id: 'NORMAL', label: 'Normal', shortLabel: 'NOR', color: 'bg-blue-500', bgColor: 'bg-blue-50' },
  { id: 'REVENTADO', label: 'Reventado', shortLabel: 'REV', color: 'bg-orange-500', bgColor: 'bg-orange-50' },
  { id: 'MEGA_REVENTADO', label: 'MegaReventado', shortLabel: 'MEGA', color: 'bg-red-500', bgColor: 'bg-red-50' },
  { id: 'MONAZOS', label: 'Monazos', shortLabel: 'MON', color: 'bg-purple-500', bgColor: 'bg-purple-50' },
  { id: 'FECHAS', label: 'Fechas', shortLabel: 'FEC', color: 'bg-green-500', bgColor: 'bg-green-50' },
  { id: 'PARLEY', label: 'Parley', shortLabel: 'PAR', color: 'bg-pink-500', bgColor: 'bg-pink-50' },
];

// Items del ticket según modalidad
export interface TicketItemNormal {
  modalidad: 'NORMAL';
  numero: string; // 2 dígitos "00"-"99"
  monto: number;
}

export interface TicketItemReventado {
  modalidad: 'REVENTADO';
  numero: string; // 2 dígitos
  montoRev1: number;
  montoRev2: number;
}

export interface TicketItemMegaReventado {
  modalidad: 'MEGA_REVENTADO';
  numero: string; // 2 dígitos
  rev1: number;   // monto directo
  rev2: number;   // monto reventado (0 si no aplica)
}

export type MonazoTipo = 'ORDEN' | 'DESORDEN' | 'P2' | 'U2';

export interface TicketItemMonazos {
  modalidad: 'MONAZOS';
  d1: string; // 1 dígito
  d2: string; // 1 dígito
  d3: string; // 1 dígito
  monto: number;
  tipo: MonazoTipo;
}

export interface TicketItemFechas {
  modalidad: 'FECHAS';
  dia: number; // 1-31
  mes: string; // nombre del mes
  monto: number;
}

export interface TicketItemParley {
  modalidad: 'PARLEY';
  num1: string; // 2 dígitos
  num2: string; // 2 dígitos
  monto: number;
}

// Unión de todos los tipos de items
export type TicketItemData = 
  | TicketItemNormal 
  | TicketItemReventado 
  | TicketItemMegaReventado 
  | TicketItemMonazos 
  | TicketItemFechas 
  | TicketItemParley;

// Item completo del ticket con ID y timestamp
export interface TicketItem {
  id: string;
  data: TicketItemData;
  timestamp: number;
}

// Métodos de pago
export type PaymentMethod = 'EFECTIVO' | 'SINPE' | 'TRANSFERENCIA' | 'CREDITO';

// Meses para el formulario de Fechas
export const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Función para calcular el total de un item
export function calcularTotalItem(item: TicketItemData): number {
  switch (item.modalidad) {
    case 'NORMAL':
      return item.monto;
    case 'REVENTADO':
      return item.montoRev1 + item.montoRev2;
    case 'MEGA_REVENTADO':
      return item.rev1 + item.rev2;
    case 'MONAZOS':
      return item.monto;
    case 'FECHAS':
      return item.monto;
    case 'PARLEY':
      return item.monto;
    default:
      return 0;
  }
}

// Función para formatear número a 2 dígitos
export function formatNumber2(value: string | number): string {
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(num) || num < 0) return '00';
  if (num > 99) return '99';
  return num.toString().padStart(2, '0');
}

// Función para formatear número a 1 dígito
export function formatNumber1(value: string | number): string {
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(num) || num < 0) return '0';
  if (num > 9) return '9';
  return num.toString();
}

// Función para obtener resumen de un item
export function getItemSummary(item: TicketItemData): string {
  switch (item.modalidad) {
    case 'NORMAL':
      return `#${item.numero}`;
    case 'REVENTADO':
      return `#${item.numero} Rev`;
    case 'MEGA_REVENTADO':
      return `#${item.numero} MegaRev`;
    case 'MONAZOS':
      return `${item.d1}${item.d2}${item.d3} ${item.tipo}`;
    case 'FECHAS':
      return `${item.dia} ${item.mes.substring(0, 3)}`;
    case 'PARLEY':
      return `#${item.num1}+#${item.num2}`;
    default:
      return '';
  }
}

/**
 * Adapta un item cuando se jala un ticket a un sorteo diferente.
 * Solo convierte tipos incompatibles; mantiene el número y los montos relevantes.
 *
 * Reglas:
 *  Mega → Normal  : queda NORMAL con numero + monto
 *  Normal → Mega  : queda MEGA_REVENTADO con numero + monto (rev1/rev2 = 0)
 *  Reventado → Normal : queda NORMAL con numero + montoRev1
 *  Reventado → Mega   : sin conversión (compatible)
 *  Todo lo demás      : sin conversión
 */
export function adaptarItemParaSorteo(item: TicketItemData, destino: SorteoType): TicketItemData {
  const m = item.modalidad;

  // Mega → Normal
  if (destino === 'TICA' && m === 'MEGA_REVENTADO') {
    return { modalidad: 'NORMAL', numero: item.numero, monto: item.rev1 };
  }

  // Normal → Mega
  if (destino === 'MEGA' && m === 'NORMAL') {
    return { modalidad: 'MEGA_REVENTADO', numero: item.numero, rev1: item.monto, rev2: 0 };
  }

  // Reventado → Normal
  if (destino === 'TICA' && m === 'REVENTADO') {
    return { modalidad: 'NORMAL', numero: item.numero, monto: item.montoRev1 };
  }

  // Reventado → Mega: no afecta (pasa igual)
  // Todos los demás: sin conversión
  return item;
}

// Estructura de tickets por sorteo
export type TicketsBySorteo = Record<SorteoType, TicketItem[]>;

// Crear estructura inicial vacía
export function createEmptyTicketsBySorteo(): TicketsBySorteo {
  return {
    TICA: [],
    REVENTADO: [],
    MEGA: [],
    MONAZOS: [],
    FECHAS: [],
    PARLEY: [],
  };
}

// Mapeo de sorteo a modalidad para el formulario
export function getSorteoModalidad(sorteo: SorteoType): BetModalidad {
  switch (sorteo) {
    case 'TICA': return 'NORMAL';
    case 'REVENTADO': return 'REVENTADO';
    case 'MEGA': return 'MEGA_REVENTADO';
    case 'MONAZOS': return 'MONAZOS';
    case 'FECHAS': return 'FECHAS';
    case 'PARLEY': return 'PARLEY';
  }
}
